import { useEffect, useState } from "react";
import { Outlet } from "react-router-dom";
import {
  LayoutDashboard, ClipboardCheck, Store, Network,
  Users, BarChart3, ShieldCheck, ScrollText, Settings as SettingsIcon,
  Hash, MapPin, FolderKanban, Briefcase, ReceiptText, CreditCard, Calculator, CircleDollarSign, Trash2, Layers,
} from "lucide-react";
import PortalLayout from "@/components/PortalLayout";
import PendingApproval from "@/pages/PendingApproval";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

import { ADMIN_ROLE, PURCHASING_ROLES, roleLabel } from "@/constants/roles";

// Accounting/admins work the whole page; purchasers and PMs approve the
// vendor invoices on their POs there.
const ACCOUNTING_PAGE_ROLES = [...PURCHASING_ROLES, "accounting"];

// adminOnly items are hidden for non-admin roles; items/sections with a
// `roles` list show only for those roles. The API enforces the real gate.
const NAV = [
  { title: null, items: [{ label: "Dashboard", path: "/contractor", end: true, icon: LayoutDashboard, testId: "dashboard" }] },
  {
    title: "Procurement",
    items: [
      // Meeting 3: no separate Purchase Requests — a PO's draft/approval
      // stage lives in Purchase Orders, and RFQs are a type of PO there too.
      { label: "Purchase Orders", path: "/contractor/purchase-orders", icon: ClipboardCheck, testId: "purchase-orders" },
      { label: "Catalog", path: "/contractor/catalog", icon: Store, testId: "catalog" },
      { label: "Expense Log", path: "/contractor/expense-log", icon: ReceiptText, testId: "expense-log" },
      { label: "Ramp Transactions", path: "/contractor/card-transactions", icon: CreditCard, testId: "card-transactions" },
      // 2026-10-27: Ramp, Expense Log, POs and AP invoices in one list —
      // purchasers see their own, admins everyone's.
      { label: "All Transactions", path: "/contractor/transactions", icon: Layers, testId: "all-transactions" },
    ],
  },
  {
    title: "Accounting",
    items: [
      // Meeting 3: Invoices + Review Queue are one page.
      { label: "Accounting", path: "/contractor/accounting", icon: Calculator, testId: "accounting", roles: ACCOUNTING_PAGE_ROLES },
    ],
  },
  {
    title: "Company",
    items: [
      { label: "Vendor Network", path: "/contractor/vendor-network", icon: Network, testId: "vendor-network" },
      { label: "Team & Permissions", path: "/contractor/team", icon: Users, testId: "team", adminOnly: true },
      { label: "Approval Rules", path: "/contractor/approval-rules", icon: ShieldCheck, testId: "approval-rules", adminOnly: true },
      { label: "Jobs & Cost Codes", path: "/contractor/jobs", icon: Briefcase, testId: "jobs", adminOnly: true },
      { label: "Delivery Addresses", path: "/contractor/delivery-addresses", icon: MapPin, testId: "delivery-addresses", adminOnly: true },
      { label: "Purchasing Contexts", path: "/contractor/purchasing-contexts", icon: FolderKanban, testId: "purchasing-contexts", adminOnly: true },
      { label: "Purchaser Report", path: "/contractor/reports/purchaser", icon: BarChart3, testId: "purchaser-report", adminOnly: true },
      { label: "Audit Trail", path: "/contractor/audit-trail", icon: ScrollText, testId: "audit-trail" },
      { label: "Recently Deleted", path: "/contractor/recently-deleted", icon: Trash2, testId: "recently-deleted", adminOnly: true },
    ],
  },
  { title: "Settings", items: [{ label: "Settings", path: "/contractor/settings", icon: SettingsIcon, testId: "settings", adminOnly: true }] },
];

function navForRole(role) {
  return NAV.map((section) => ({
    ...section,
    items: section.items.filter((item) => {
      if (item.roles) return item.roles.includes(role);
      if (item.adminOnly) return role === ADMIN_ROLE;
      return true;
    }),
  })).filter((section) => section.items.length > 0);
}

export default function ContractorLayout() {
  const [company, setCompany] = useState(null);
  const { user } = useAuth();
  const active = user?.tenant_status === "active";

  useEffect(() => {
    if (active) api.get("/contractor/settings").then((r) => setCompany(r.data)).catch(() => {});
  }, [active]);

  if (!active) return <PendingApproval portalLabel="Contractor Portal" />;

  return (
    <PortalLayout portalLabel="Contractor Portal" tenantName={company?.name} tenantSubtitle={roleLabel(user?.role)} navSections={navForRole(user?.role)} showSearch>
      <Outlet />
    </PortalLayout>
  );
}
